import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Plus, X, TrendingUp, Sparkles, ArrowUpRight, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface TrendingQuery {
    keyword: string;
    value: number;
    type: "rising" | "top";
}

interface TopicResult {
    topic: string;
    risingQueries: TrendingQuery[];
    topQueries: TrendingQuery[];
}

export default function MarketRadar() {
    const [newTopic, setNewTopic] = useState("");
    const [topics, setTopics] = useState<string[]>([]);

    // Fetch tracked topics
    const trackedTopicsQuery = trpc.trends.getTrackedTopics.useQuery();
    const setTrackedTopicsMutation = trpc.trends.setTrackedTopics.useMutation({
        onSuccess: () => {
            trackedTopicsQuery.refetch();
            toast.success("主題已更新");
        },
    });

    // Explore topics (manual trigger with refetch)
    const exploreQuery = trpc.trends.exploreTopics.useQuery(
        { topics },
        { enabled: false } // Disable auto-fetch, use manual trigger
    );

    // Add to monitoring mutation
    const addToMonitoringMutation = trpc.trends.addToMonitoring.useMutation({
        onSuccess: (result) => {
            if (result.success) {
                toast.success("已加入關鍵字監控");
            } else {
                toast.error(result.message || "加入失敗");
            }
        },
        onError: (error) => {
            toast.error(`加入失敗: ${error.message}`);
        },
    });

    // Sync local state with server
    useEffect(() => {
        if (trackedTopicsQuery.data) {
            setTopics(trackedTopicsQuery.data);
        }
    }, [trackedTopicsQuery.data]);

    const handleAddTopic = () => {
        if (!newTopic.trim()) return;
        if (topics.includes(newTopic.trim())) {
            toast.error("主題已存在");
            return;
        }
        if (topics.length >= 5) {
            toast.error("最多只能追蹤 5 個主題");
            return;
        }

        const updatedTopics = [...topics, newTopic.trim()];
        setTopics(updatedTopics);
        setTrackedTopicsMutation.mutate({ topics: updatedTopics });
        setNewTopic("");
    };

    const handleRemoveTopic = (topic: string) => {
        const updatedTopics = topics.filter((t) => t !== topic);
        setTopics(updatedTopics);
        setTrackedTopicsMutation.mutate({ topics: updatedTopics });
    };

    const handleExplore = () => {
        if (topics.length === 0) {
            toast.error("請先新增至少一個主題");
            return;
        }
        exploreQuery.refetch();
    };

    const handleAddToMonitoring = (keyword: string, topic: string) => {
        addToMonitoringMutation.mutate({ keyword, category: topic });
    };

    return (
        <DashboardLayout>
            <div className="container py-8 space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-4xl font-bold flex items-center gap-3">
                        <Sparkles className="h-8 w-8 text-yellow-500" />
                        Market Radar
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        探索市場正在搜尋什麼，發現未知的流量機會
                    </p>
                </div>

                {/* Topic Manager */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5" />
                            追蹤主題
                        </CardTitle>
                        <CardDescription>
                            設定 3-5 個核心主題，點擊「開始探索」抓取相關的飆升關鍵字
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex gap-2 mb-4">
                            <Input
                                placeholder="輸入主題，如：植牙、隱適美..."
                                value={newTopic}
                                onChange={(e) => setNewTopic(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAddTopic()}
                                disabled={setTrackedTopicsMutation.isPending}
                            />
                            <Button
                                onClick={handleAddTopic}
                                disabled={!newTopic.trim() || setTrackedTopicsMutation.isPending || topics.length >= 5}
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                新增
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {topics.map((topic) => (
                                <Badge key={topic} variant="secondary" className="text-sm px-3 py-1">
                                    {topic}
                                    <button
                                        onClick={() => handleRemoveTopic(topic)}
                                        className="ml-2 hover:text-destructive"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </Badge>
                            ))}
                            {topics.length === 0 && (
                                <p className="text-muted-foreground text-sm">
                                    尚未設定任何主題，請新增主題開始探索
                                </p>
                            )}
                        </div>
                        {/* Explore Button */}
                        {topics.length > 0 && (
                            <Button
                                onClick={handleExplore}
                                disabled={exploreQuery.isFetching}
                                className="w-full"
                                size="lg"
                            >
                                {exploreQuery.isFetching ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        正在探索...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        開始探索
                                    </>
                                )}
                            </Button>
                        )}
                    </CardContent>
                </Card>

                {/* Error State */}
                {exploreQuery.isError && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                            探索失敗：{exploreQuery.error?.message || "請確認 DataForSEO API Key 已正確設定"}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Loading State */}
                {exploreQuery.isFetching && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        <span className="ml-3 text-muted-foreground">正在探索市場趨勢...</span>
                    </div>
                )}

                {/* Breakout Keywords */}
                {exploreQuery.data && (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {exploreQuery.data.map((result: TopicResult) => (
                            <Card key={result.topic} className="overflow-hidden">
                                <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5">
                                    <CardTitle className="text-lg">{result.topic}</CardTitle>
                                    <CardDescription>
                                        {result.risingQueries.length} 個飆升詞 · {result.topQueries.length} 個熱門詞
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="pt-4 space-y-4">
                                    {/* Rising Queries */}
                                    {result.risingQueries.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-orange-600 mb-2 flex items-center gap-1">
                                                <ArrowUpRight className="h-4 w-4" />
                                                飆升關鍵字
                                            </h4>
                                            <div className="space-y-2">
                                                {result.risingQueries.slice(0, 5).map((q) => (
                                                    <div
                                                        key={q.keyword}
                                                        className="flex items-center justify-between group"
                                                    >
                                                        <span className="text-sm">{q.keyword}</span>
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="outline" className="text-orange-600">
                                                                +{q.value}%
                                                            </Badge>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity h-6 px-2"
                                                                onClick={() => handleAddToMonitoring(q.keyword, result.topic)}
                                                                disabled={addToMonitoringMutation.isPending}
                                                            >
                                                                <Plus className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Top Queries */}
                                    {result.topQueries.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-blue-600 mb-2">
                                                熱門關鍵字
                                            </h4>
                                            <div className="space-y-2">
                                                {result.topQueries.slice(0, 5).map((q) => (
                                                    <div
                                                        key={q.keyword}
                                                        className="flex items-center justify-between group"
                                                    >
                                                        <span className="text-sm">{q.keyword}</span>
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="outline" className="text-blue-600">
                                                                {q.value}
                                                            </Badge>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity h-6 px-2"
                                                                onClick={() => handleAddToMonitoring(q.keyword, result.topic)}
                                                                disabled={addToMonitoringMutation.isPending}
                                                            >
                                                                <Plus className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* No data */}
                                    {result.risingQueries.length === 0 && result.topQueries.length === 0 && (
                                        <p className="text-muted-foreground text-sm text-center py-4">
                                            暫無相關趨勢數據
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Empty State */}
                {topics.length === 0 && !exploreQuery.isLoading && (
                    <Card className="border-dashed">
                        <CardContent className="py-12 text-center">
                            <Sparkles className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                            <h3 className="text-lg font-medium mb-2">開始探索市場趨勢</h3>
                            <p className="text-muted-foreground max-w-md mx-auto">
                                在上方輸入您的核心主題（如「植牙」、「矯正」），<br />
                                系統會自動從 Google Trends 發現正在飆升的相關關鍵字。
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </DashboardLayout>
    );
}
